import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFlag,
  faHeart as faHeartSolid,
} from "@fortawesome/free-solid-svg-icons";
import { faHeart as faHeartRegular } from "@fortawesome/free-regular-svg-icons";
import { API_ENDPOINTS } from "../../config/constants";

// Mention Input Component with autocomplete
export const MentionInput = ({
  value,
  onChange,
  onSubmit,
  placeholder,
  autoFocus = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [mentionStart, setMentionStart] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const searchUsers = async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(
        `${API_ENDPOINTS.USER_SEARCH}?q=${encodeURIComponent(query)}`,
        {
          credentials: "include",
        }
      );
      if (res.ok) {
        const users = await res.json();
        setSuggestions(users);
        setShowSuggestions(users.length > 0);
        setSelectedIndex(-1);
      }
    } catch (err) {
      console.error("Error searching users:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart;

    onChange(newValue);

    // Check if we're typing after an @ symbol
    const textBeforeCursor = newValue.substring(0, cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      const mentionQuery = mentionMatch[1];
      const start = cursorPosition - mentionQuery.length;
      setMentionStart(start);
      searchUsers(mentionQuery);
    } else {
      setShowSuggestions(false);
      setMentionStart(-1);
    }
  };

  const handleKeyDown = (e) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
      } else if (e.key === "Enter" && selectedIndex >= 0) {
        e.preventDefault();
        selectUser(suggestions[selectedIndex]);
        return;
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    }

    if (e.key === "Enter" && !e.shiftKey && !showSuggestions) {
      e.preventDefault();
      onSubmit(e);
    }
  };

  const selectUser = (user) => {
    const cursorPosition = inputRef.current.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPosition);
    const textAfterCursor = value.substring(cursorPosition);

    // Find the @ symbol position
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    if (mentionMatch) {
      const atPosition = cursorPosition - mentionMatch[0].length;
      const newValue =
        value.substring(0, atPosition) + `@${user.username} ` + textAfterCursor;

      onChange(newValue);

      // Set cursor position after the mention
      setTimeout(() => {
        const newCursorPos = atPosition + user.username.length + 2;
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        inputRef.current.focus();
      }, 0);
    }

    setShowSuggestions(false);
    setSelectedIndex(-1);
    setMentionStart(-1);
  };

  const renderDropdown = () => {
    if (!showSuggestions || suggestions.length === 0) return null;

    return (
      <div className="absolute z-[9999] w-full bg-white border border-gray-300 rounded-md shadow-lg dark:bg-gray-800 dark:border-gray-700 max-h-48 overflow-y-auto bottom-full mb-1 flex flex-col-reverse">
        <div className="flex flex-col">
          {suggestions.map((user, index) => (
            <button
              key={user._id}
              type="button"
              onClick={() => selectUser(user)}
              className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 ${
                index === selectedIndex ? "bg-gray-100 dark:bg-gray-700" : ""
              }`}
            >
              {user.profilePicVariants?.small ? (
                <img
                  src={user.profilePicVariants.small}
                  alt={user.username}
                  className="w-6 h-6 rounded-full"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-xs">
                  {user.username[0].toUpperCase()}
                </div>
              )}
              <span className="text-sm dark:text-gray-300">
                @{user.username}
              </span>
            </button>
          ))}
          {isLoading && (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
              {t("common.loading")}...
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div ref={containerRef} className={`relative ${className || "w-full"}`}>
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        className="w-full bg-white border border-gray-300 rounded-md px-3 py-1.5 text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
      />
      {renderDropdown()}
    </div>
  );
};

export const ReplyForm = ({
  onSubmit,
  text,
  setText,
  error,
  autoFocus = false,
}) => {
  const { t } = useTranslation();

  return (
    <form onSubmit={onSubmit} className="mt-1 flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <MentionInput
          value={text}
          onChange={setText}
          onSubmit={onSubmit}
          placeholder={t("discussion.reply_placeholder")}
          autoFocus={autoFocus}
          className="flex-grow"
        />
        <button
          type="submit"
          className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-1.5 rounded text-sm hover:from-primary-700 hover:to-primary-800 active:from-primary-800 active:to-primary-900 transition-all duration-300 dark:bg-primary-600 dark:hover:bg-primary-700 dark:active:bg-primary-800 dark:active:to-primary-900"
        >
          {t("discussion.post")}
        </button>
      </div>
      {error && (
        <p className="text-red-500 text-xs dark:text-red-400">{error}</p>
      )}
    </form>
  );
};

export const EditForm = ({ text, setText, onSave, onCancel }) => {
  const { t } = useTranslation();

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave();
  };

  return (
    <div className="mt-1">
      <form onSubmit={handleSubmit}>
        <MentionInput
          value={text}
          onChange={setText}
          onSubmit={handleSubmit}
          placeholder={t("discussion.edit_placeholder")}
          className="w-full p-2 border rounded"
        />
        <div className="mt-1 space-x-2">
          <button
            type="submit"
            className="text-xs bg-blue-500 text-white px-2 py-1 rounded dark:bg-primary-600 dark:text-white dark:hover:bg-primary-700"
          >
            {t("actions.save")}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="text-xs bg-gray-500 text-white px-2 py-1 rounded dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            {t("actions.cancel")}
          </button>
        </div>
      </form>
    </div>
  );
};

export const ReportModal = ({ onClose, onSubmit, reason, setReason }) => {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white p-4 rounded-lg max-w-md w-full dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold mb-4 dark:text-gray-100">
          {t("discussion.report_modal.title")}
        </h3>
        <form onSubmit={onSubmit}>
          <input
            type="text"
            placeholder={t("discussion.report_modal.placeholder")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full p-2 mb-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border rounded dark:text-gray-400 dark:border-gray-700 dark:hover:bg-gray-600"
            >
              {t("discussion.report_modal.cancel")}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-red-500 text-white rounded dark:bg-red-600 dark:text-white dark:hover:bg-red-700"
            >
              {t("discussion.report_modal.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const CommentActions = ({
  onEdit,
  onDelete,
  onReport,
  isOwner,
  user,
}) => {
  const { t } = useTranslation();
  return (
    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 dark:text-gray-400">
      {user && (
        <>
          <button
            onClick={onReport}
            className="text-red-200 hover:text-red-400 dark:text-red-400 dark:hover:text-red-600"
            title={t("discussion.report")}
          >
            <FontAwesomeIcon icon={faFlag} />
          </button>
          {isOwner && (
            <>
              <button
                onClick={onEdit}
                className="text-blue-500 hover:text-blue-600"
              >
                {t("discussion.edit")}
              </button>
              <button
                onClick={onDelete}
                className="text-red-500 hover:text-red-600"
              >
                {t("discussion.delete")}
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export const CommentLikeButton = ({
  commentId,
  initialLikesCount = 0,
  initialLiked = false,
  user,
  onLike,
}) => {
  const { t } = useTranslation();
  const [liked, setLiked] = useState(initialLiked);
  const [likesCount, setLikesCount] = useState(initialLikesCount);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleLike = async () => {
    if (!user) return;

    try {
      setIsAnimating(true);
      const result = await onLike(commentId);
      setLiked(result.liked);
      setLikesCount(result.likesCount);

      // Reset animation after a short delay
      setTimeout(() => setIsAnimating(false), 300);
    } catch (err) {
      console.error("Error liking comment:", err);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  if (likesCount === 0 && !user) {
    return null; // Don't show if no likes and user not logged in
  }

  return (
    <button
      onClick={handleLike}
      className={`flex items-center gap-1 text-xs transition-all duration-200 ${
        liked
          ? "text-red-500 hover:text-red-600"
          : "text-gray-400 hover:text-red-400"
      } ${!user && "opacity-50 cursor-not-allowed"}`}
      disabled={!user}
      title={
        user
          ? liked
            ? t("discussion.unlike")
            : t("discussion.like")
          : t("common.login_required")
      }
    >
      <div
        className={`transform transition-all duration-300 ${
          isAnimating ? "scale-125" : "scale-100"
        }`}
      >
        <FontAwesomeIcon
          icon={liked ? faHeartSolid : faHeartRegular}
          className="w-3 h-3"
        />
      </div>
      {likesCount > 0 && <span>{likesCount}</span>}
    </button>
  );
};

// Component to render text with clickable mentions
export const MentionText = ({ text, mentions = [] }) => {
  if (!text) return null;

  // If no mentions, return plain text
  if (!mentions.length) {
    return <span>{text}</span>;
  }

  // Create a map of usernames to mention data for quick lookup
  const mentionMap = mentions.reduce((acc, mention) => {
    acc[mention.username] = mention;
    return acc;
  }, {});

  // Split text and replace mentions with links
  const parts = [];
  let lastIndex = 0;

  // Find all @mentions in the text
  const mentionRegex = /@(\w+)/g;
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    const username = match[1];
    const startIndex = match.index;
    const endIndex = startIndex + match[0].length;

    // Add text before the mention
    if (startIndex > lastIndex) {
      parts.push(text.substring(lastIndex, startIndex));
    }

    // Add the mention as a link if it's a valid mention
    if (mentionMap[username]) {
      parts.push(
        <Link
          key={`mention-${startIndex}`}
          to={`/u/${username}`}
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 font-medium"
        >
          @{username}
        </Link>
      );
    } else {
      // If not a valid mention, render as plain text
      parts.push(match[0]);
    }

    lastIndex = endIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return <span>{parts}</span>;
};

// PropTypes
MentionInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
};

MentionText.propTypes = {
  text: PropTypes.string,
  mentions: PropTypes.arrayOf(
    PropTypes.shape({
      username: PropTypes.string.isRequired,
      user: PropTypes.string,
      position: PropTypes.number,
    })
  ),
};

ReplyForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  text: PropTypes.string.isRequired,
  setText: PropTypes.func.isRequired,
  error: PropTypes.string,
  autoFocus: PropTypes.bool,
};

EditForm.propTypes = {
  text: PropTypes.string.isRequired,
  setText: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

ReportModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  reason: PropTypes.string.isRequired,
  setReason: PropTypes.func.isRequired,
};

CommentActions.propTypes = {
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onReport: PropTypes.func,
  isOwner: PropTypes.bool,
  user: PropTypes.object,
};

CommentLikeButton.propTypes = {
  commentId: PropTypes.string.isRequired,
  initialLikesCount: PropTypes.number,
  initialLiked: PropTypes.bool,
  user: PropTypes.object,
  onLike: PropTypes.func.isRequired,
};
